"use client";

import classNames from "classnames";
import React, { useEffect, useState } from "react";
import CustomButton from "rivals/components/shared/CustomButton";
import PremiumContent from "rivals/components/shared/Icons/PremiumContent";
import { setData } from "rivals/shared/utils/qa";
import styles from "./Gallery.module.scss";
import GalleryModal from "./GalleryModal";
import strings from "./strings";
import { Props } from "./types";

const Gallery = ({
  activeAthleteView,
  animatedDescription = true,
  gallery,
  error,
  galleryDetails,
  isSubscribedUser,
  onClose,
  onOpen,
  prospect
}: Props): JSX.Element | null => {
  const [modalVisible, setModalVisible] = useState(false);
  const [liveDate, setLiveDate] = useState("");

  // avoid hydration issues by calling toLocaleDateString after component renders
  useEffect(() => {
    if (gallery?.liveAt) {
      setLiveDate(new Date(gallery?.liveAt).toLocaleDateString());
    }
  }, [gallery?.liveAt]);

  if (!gallery) {
    return null;
  }

  const { images, imagesCount, name, premium, summary } = gallery;
  const galleryPreviewUrl =
    images && images.length > 0 ? images[0].galleryPreviewUrl : "";

  const toggleModal = (open: boolean): void => {
    setModalVisible(open);
    if (open) {
      onOpen && onOpen();
    } else {
      onClose && onClose();
    }
  };

  const animatedContainer = (
    <div className={styles.animatedContainer}>
      <div className={styles.galleryName}>
        {premium && <PremiumContent />}
        {name}
      </div>
      <div className={styles.galleryDateCount}>
        <img
          alt="gallery icon"
          className={styles.galleryIcon}
          src={strings.iconGalleryImage}
        />
        {`${liveDate} - ${imagesCount}`}
      </div>
      <div className={styles.gallerySummary}>{summary}</div>
    </div>
  );

  const description = (
    <div className={styles.descriptionContainer}>
      <div className={styles.galleryName}>{name}</div>
      <div className={styles.galleryDateCount}>
        {premium && <PremiumContent />}
        {liveDate}
      </div>
    </div>
  );

  return (
    <>
      <div
        className={classNames(styles.galleryImageContainer, {
          [styles.animated]: animatedDescription
        })}
        {...setData("gallery-image")}
      >
        {animatedDescription && animatedContainer}
        <CustomButton
          ariaLabel={gallery.description}
          className={styles.galleryImage}
          icon={<img alt={gallery.description} src={galleryPreviewUrl} />}
          onClick={() => toggleModal(true)}
        />
        {!animatedDescription && description}
      </div>
      {modalVisible && (
        <GalleryModal
          activeAthleteView={activeAthleteView}
          error={error}
          gallery={galleryDetails}
          isSubscribedUser={isSubscribedUser}
          onClose={() => toggleModal(false)}
          open={modalVisible}
          prospect={prospect}
        />
      )}
    </>
  );
};

export default Gallery;
